import { useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '@ibk/shared-utils';
export function useCurrencyInput(value, onChange, currency, min, max, clampValue = true) {
    const DECIMAL_PLACES = 2;
    const stepDecimal = useMemo(() => Number((1 / Math.pow(10, DECIMAL_PLACES)).toFixed(DECIMAL_PLACES)), []);
    const minValue = typeof min === 'number' ? min : undefined;
    const maxValue = typeof max === 'number' ? max : undefined;
    const [focused, setFocused] = useState(false);
    const isBRL = currency === 'BRL';
    const [text, setText] = useState(() => {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return formatCurrency(roundToDecimals(value), currency);
        }
        return '';
    });
    useEffect(() => {
        if (focused)
            return;
        if (typeof value === 'number' && Number.isFinite(value)) {
            setText(formatCurrency(roundToDecimals(value), currency));
        }
        else {
            setText('');
        }
    }, [value, currency, focused]);
    function clamp(localValue) {
        let result = localValue;
        if (typeof minValue === 'number')
            result = Math.max(result, minValue);
        if (typeof maxValue === 'number')
            result = Math.min(result, maxValue);
        return result;
    }
    function roundToDecimals(localValue) {
        return Math.round(localValue * Math.pow(10, DECIMAL_PLACES)) / Math.pow(10, DECIMAL_PLACES);
    }
    function sanitizeNumericText(raw) {
        let result = '';
        let seenSeparator = false;
        let fractionLength = 0;
        for (const ch of String(raw)) {
            if (ch >= '0' && ch <= '9') {
                if (seenSeparator) {
                    if (fractionLength >= DECIMAL_PLACES)
                        continue;
                    fractionLength += 1;
                }
                result += ch;
                continue;
            }
            if ((ch === '.' || ch === ',') && !seenSeparator) {
                seenSeparator = true;
                result += ch;
            }
        }
        return result;
    }
    function stripBRLPrefix(raw) {
        return isBRL ? raw.replace(/^R\$\s*/, '').trim() : raw;
    }
    function parseTextToNumber(currentText) {
        const numericPart = stripBRLPrefix(currentText);
        const normalized = numericPart.replace(',', '.');
        if (!normalized || normalized === '.' || normalized === ',')
            return undefined;
        const parsed = parseFloat(normalized);
        if (!Number.isFinite(parsed))
            return undefined;
        const rounded = roundToDecimals(parsed);
        return clampValue ? clamp(rounded) : rounded;
    }
    function onFocus() {
        setFocused(true);
        if (typeof value === 'number' && Number.isFinite(value)) {
            setText(formatCurrency(roundToDecimals(value), currency));
        }
    }
    function onBlur() {
        setFocused(false);
        const parsed = parseTextToNumber(text);
        onChange(parsed);
        if (typeof parsed === 'number') {
            setText(formatCurrency(parsed, currency));
        }
        else {
            setText('');
        }
    }
    function onKeyDownNumeric(e) {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab', '.', ','];
        if (allowedKeys.includes(e.key))
            return;
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    }
    const BRL_PREFIX = 'R$ ';
    const MAX_LENGTH = 100;
    function onTextChange(event) {
        const raw = event.target.value ?? '';
        let numericPart = sanitizeNumericText(stripBRLPrefix(raw));
        if (isBRL && numericPart.length > MAX_LENGTH - BRL_PREFIX.length) {
            numericPart = numericPart.slice(0, MAX_LENGTH - BRL_PREFIX.length);
        }
        let newText;
        if (isBRL) {
            newText = numericPart ? `${BRL_PREFIX}${numericPart}` : '';
        }
        else {
            newText = numericPart;
        }
        setText(newText);
        const parsed = parseTextToNumber(newText);
        onChange(parsed);
    }
    return {
        text,
        setText,
        onTextChange,
        onFocus,
        onBlur,
        onKeyDownNumeric,
        stepDecimal,
    };
}
